import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  Report,
  ReportsCollection,
  ScheduledTask,
  ScheduledTaskReportRule,
  ScheduledTasksCollection,
  ScheduledTaskState,
  TaskBulkActionDto,
  UpdateReportDto,
} from '../models/scheduled-task.model';
import { Supplier } from '../models/supplier.model';
import { TestCaseTemplate } from '../models/test-case-template.model';
import { FLASH_CALL_TEST_STATUS, LIVE_NUMBER_TEST_STATUSES, MESSAGE_STATES } from '../models/test-group.model';
import { CustomUtils } from './custom-utils';
import { LocalStorageService } from './localStorage.service';
import { RestUtils } from './rest-utils';
import { SuppliersService } from './suppliers.service';
import { TestCaseTemplatesService } from './test-case-template.service';
import { TestResultUtils } from './test-result-utils';
declare const moment: {
  (input?: string | number | Date): { format: (pattern: string) => string };
  duration: (value: number, unit: string) => { humanize: () => string };
};

@Injectable()
export class SchedulerService {
  http: HttpClient;

  utils = new RestUtils();

  headers = new HttpHeaders();

  private actionSubject: Subject<SchedulerTaskActionData> = new Subject<SchedulerTaskActionData>();
  private reportActionSubject: Subject<SchedulerReportActionData> = new Subject<SchedulerReportActionData>();
  action$ = this.actionSubject.asObservable();
  reportsAction$ = this.reportActionSubject.asObservable();

  static SCHEDULER_STATUSES = [
    ScheduledTaskState.RUNNING,
    ScheduledTaskState.SCHEDULED,
    ScheduledTaskState.PAUSED,
    ScheduledTaskState.STOPPED,
    ScheduledTaskState.FINISHED,
  ];
  static ACTIVE_SCHEDULER_STATUSES = [ScheduledTaskState.RUNNING, ScheduledTaskState.SCHEDULED, ScheduledTaskState.PAUSED];
  static INACTIVE_SCHEDULER_STATUSES = [ScheduledTaskState.STOPPED, ScheduledTaskState.FINISHED];
  static DAYS = [
    {
      label: 'S',
      value: 'SUNDAY',
    },
    {
      label: 'M',
      value: 'MONDAY',
    },
    {
      label: 'T',
      value: 'TUESDAY',
    },
    {
      label: 'W',
      value: 'WEDNESDAY',
    },
    {
      label: 'T',
      value: 'THURSDAY',
    },
    {
      label: 'F',
      value: 'FRIDAY',
    },
    {
      label: 'S',
      value: 'SATURDAY',
    },
  ];

  static REPORT_RULE_TEST_STATUSES = [...LIVE_NUMBER_TEST_STATUSES, FLASH_CALL_TEST_STATUS];

  static HIDDEN_STORAGE_KEY = 'sch-hidden-mode-inactive';
  static SHOW_MY_TASKS_FLAG_KEY_PREFIX = 'sch-show-my-tasks-flag';

  constructor(
    http: HttpClient,
    public storage: LocalStorageService,
    private testCaseTemplateService: TestCaseTemplatesService,
    private suppliersService: SuppliersService
  ) {
    this.http = http;
    this.headers = this.headers.set('Content-Type', 'application/json');
  }

  announceAction(action: SchedulerTaskActionData) {
    this.actionSubject.next(action);
  }
  announceReportAction(action: SchedulerReportActionData) {
    this.reportActionSubject.next(action);
  }

  static handleReportRuleDto(currentRow: Report) {
    const reportRuleDto: ScheduledTaskReportRule = currentRow.reportRuleDto;
    const messageState = reportRuleDto.messageState ?? '';
    const testStatus = reportRuleDto.testStatus ?? '';
    const repetitionsTarget = reportRuleDto.repetitionsTarget ?? '-';

    let messageStateLabel = MESSAGE_STATES.find(state => state.id === messageState)?.label ?? '-';
    let testStatusLabel = SchedulerService.REPORT_RULE_TEST_STATUSES.find(state => state.id === testStatus)?.label ?? '-';

    if (reportRuleDto.messageStateWaitingTime && messageStateLabel !== '-') {
      const messageStateTime = SchedulerService.secondsToHuman(reportRuleDto.messageStateWaitingTime);
      messageStateLabel += ' >= ' + messageStateTime.value + messageStateTime.unit;
    }
    if (reportRuleDto.testStatusWaitingTime && testStatusLabel !== '-') {
      const testStatusTime = SchedulerService.secondsToHuman(reportRuleDto.testStatusWaitingTime);
      testStatusLabel += ' >= ' + testStatusTime.value + testStatusTime.unit;
    }

    const messageStateIcon = messageState ? `<i class="fs-3 me-0_5 ${CustomUtils.getIconForStatus(messageState)}"></i>` : '';
    const testStatusIcon = testStatus ? `<i class="fs-3 mx-0_5 ${CustomUtils.getIconForStatus(testStatus)}"></i>` : '';

    return `
        <div class="d-flex justify-content-between align-items-center">
            ${messageStateIcon}${messageStateLabel} /
            ${testStatusIcon}${testStatusLabel} /
            ${repetitionsTarget} test reps
        </div>`;
  }

  static formatReportRule(rule: ScheduledTaskReportRule): string {
    const messageState = rule.messageState;
    const testStatus = rule.testStatus;
    const repetitionsTarget = rule.repetitionsTarget ?? '1';

    let messageStateLabel = MESSAGE_STATES.find(state => state.id === messageState)?.label ?? '-';
    const messageStateIcon = messageState ? `<i class="fs-3 me-0_5 ${CustomUtils.getIconForStatus(messageState)}"></i>` : '';

    let testStatusLabel = SchedulerService.REPORT_RULE_TEST_STATUSES.find(state => state.id === testStatus)?.label ?? '-';
    const testStatusIcon = testStatus ? `<i class="fs-3 me-0_5 ${CustomUtils.getIconForStatus(testStatus)}"></i>` : '';

    if (rule.messageStateWaitingTime && messageStateLabel !== '-') {
      const messageStateTime = SchedulerService.secondsToHuman(rule.messageStateWaitingTime);
      messageStateLabel += ' >= ' + messageStateTime.value + messageStateTime.unit;
    }
    if (rule.testStatusWaitingTime && testStatusLabel !== '-') {
      const testStatusTime = SchedulerService.secondsToHuman(rule.testStatusWaitingTime);
      testStatusLabel += ' >= ' + testStatusTime.value + testStatusTime.unit;
    }
    const messageStatePill = `
        <div class="pill me-10">
            ${messageStateIcon}
            <span class="text-neutral-n9"> ${messageStateLabel}</span>
        </div>
        `;
    const testStatusPill = `
        <div class="pill me-10">
                ${testStatusIcon}
                <span class="text-neutral-n9"> ${testStatusLabel}</span>
            </div>
        `;
    return `
        <div class="d-flex justify-content-start align-items-center mb-10">
            ${messageState ? messageStatePill : ''}
            ${messageState && testStatus ? '<span class="me-10">AND</span>' : ''}
            ${testStatus ? testStatusPill : ''}
            x ${repetitionsTarget}
        </div>
        `;
  }

  all(params: SchedulerRequestParams) {
    const queryParams = {
      page: params.page,
      size: params.size,
      hidden: params.hidden ? 'true' : 'false',
      ownTasksOnly: params.ownTasksOnly ? 'true' : 'false',
      searchString: params.search ? params.search : '',
      sort: params.sort.length ? params.sort : [],
    };

    const url = this.utils.buildUrl('ROLE/sch/tasks', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<ScheduledTasksCollection>(url, options).pipe(
      map(_ => {
        const collection = _;
        collection.content.map(task => {
          if (task.finishAt === '1970-01-01T00:00:00Z') {
            task.finishAt = null;
          }
          if (task.daysOfWeek === null) {
            task.daysOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
          }
          return task;
        });
        return collection;
      })
    );
  }

  search(params: SchedulerRequestParams) {
    const queryParams = {
      page: params.page,
      size: params.size,
      sort: params.sort.length ? params.sort : [],
    };
    const url = this.utils.buildUrl('ROLE/sch/tasks/search', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    const searchData = params.searchData ? params.searchData : this.getEmptySearchObject();

    return this.http.post<ScheduledTasksCollection>(url, searchData, options).pipe(
      map(_ => {
        const collection = _;
        collection.content.map(task => {
          if (task.finishAt === '1970-01-01T00:00:00Z') {
            task.finishAt = null;
          }
          if (task.daysOfWeek === null) {
            task.daysOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
          }
          return task;
        });
        return collection;
      })
    );
  }

  reportsAll(params: SchedulerReportRequestParams) {
    const queryParams = {
      page: params.page,
      size: params.size,
      searchString: params.search ? params.search : '',
      sort: params.sort && params.sort.length ? params.sort : [],
    };

    const url = this.utils.buildUrl('ROLE/sch/reports', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    if (params.searchData) {
      const isSearchDataPresent = Object.values(params.searchData).filter(v => v !== null && v?.length > 0).length > 0;
      if (isSearchDataPresent) {
        return this.http.post<ReportsCollection>(url, params.searchData, options);
      }
    }
    return this.http.get<ReportsCollection>(url, options);
  }

  bulkStop(taskIds: number[]) {
    const url = this.utils.buildUrl('ROLE/sch/tasks/stop', {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    const dto: TaskBulkActionDto = { taskIds };

    return this.http.post(url, dto, options);
  }

  bulkPause(taskIds: number[]) {
    const url = this.utils.buildUrl('ROLE/sch/tasks/pause', {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    const dto: TaskBulkActionDto = { taskIds };

    return this.http.post(url, dto, options);
  }

  bulkResume(taskIds: number[]) {
    const url = this.utils.buildUrl('ROLE/sch/tasks/resume', {});
    const options = this.utils.getHttpHeaderOptions(this.headers);
    const dto: TaskBulkActionDto = { taskIds };

    return this.http.post(url, dto, options);
  }

  updateReport(reportId: number, updateDto: UpdateReportDto) {
    const url = this.utils.buildUrl(`ROLE/sch/reports/${reportId}`);
    const options = this.utils.getHttpHeaderOptions(this.headers);
    return this.http.patch<Report>(url, updateDto, options);
  }

  one(id: number) {
    const url = this.utils.buildUrl(`ROLE/sch/tasks/${id}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<ScheduledTask>(url, options).pipe(
      map(_ => {
        const task = _;
        if (task.finishAt === '1970-01-01T00:00:00Z') {
          task.finishAt = null;
        }
        if (task.daysOfWeek === null) {
          task.daysOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
        }
        return task;
      })
    );
  }

  save(task: ScheduledTask, startNow = false) {
    const url = this.utils.buildUrl(`ROLE/sch/tasks`, { startNow: startNow ? 'true' : 'false' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<ScheduledTask>(url, task, options);
  }

  hide(taskId: number, hidden: boolean) {
    const url = this.utils.buildUrl(`ROLE/sch/tasks/${taskId}/hidden`, { hidden: hidden ? 'true' : 'false' });
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, { hidden: hidden }, options);
  }

  stop(taskId: number) {
    const url = this.utils.buildUrl(`ROLE/sch/tasks/${taskId}/stop`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, {}, options);
  }

  pause(taskId: number) {
    const url = this.utils.buildUrl(`ROLE/sch/tasks/${taskId}/pause`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, {}, options);
  }

  resume(taskId: number) {
    const url = this.utils.buildUrl(`ROLE/sch/tasks/${taskId}/resume`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post(url, {}, options);
  }

  getTaskSettings(scheduledTaskId: number): Observable<{ task: ScheduledTask; template: TestCaseTemplate; suppliers: Supplier[] }> {
    const scheduleTaskId = scheduledTaskId;
    return this.one(scheduleTaskId).pipe(
      switchMap((task: ScheduledTask) => {
        return forkJoin({
          task: of(task),
          template: this.testCaseTemplateService.one(task.testCaseTemplateId),
        });
      }),
      switchMap(({ task, template }) => {
        const uniqueSupplierIds = Array.from(new Set((template.routeDtos ?? []).map(route => route.supplierId)));

        if (!uniqueSupplierIds.length) {
          return of({ task, template, suppliers: [] });
        }

        return forkJoin(uniqueSupplierIds.map(supplierId => this.suppliersService.one(supplierId).pipe(catchError(() => of(null))))).pipe(
          map(suppliers => ({
            task,
            template,
            suppliers: suppliers.filter((supplier): supplier is Supplier => supplier !== null),
          }))
        );
      })
    );
  }

  formatDuration(row: ScheduledTask) {
    if (row.sendOnlyOnce) {
      return '';
    }
    if (row.randomized) {
      return `<div class="two-lines">Randomized/(${row.repeatsPerDayRandomized} per day)</div>`;
    }
    if (row.repeatEvery) {
      const t = SchedulerService.secondsToHuman(row.repeatEvery);
      const map = {
        d: 'days',
        m: 'minutes',
        h: 'hours',
        s: 'seconds',
      };
      // Generate human-readable duration and remove articles
      let humanized = moment.duration(t.value, map[t.unit]).humanize();
      humanized = humanized.replace(/\ban?\s/, ''); // Remove "a " or "an "
      return humanized;
    }
    return '';
  }

  consolidateTaskSettings(
    task: ScheduledTask,
    template: TestCaseTemplate,
    report: Report,
    suppliers: Supplier[] = [],
    totalExecutedTests: number | string = '-'
  ) {
    const sortedDays = CustomUtils.sortDays(task.daysOfWeek);

    const testsPerBatch = this.getTestsPerBatch(task, template);
    const taskStartAt = task.startAt ? (typeof task.startAt === 'number' ? task.startAt : Date.parse(task.startAt) / 1000) : 0;
    const isTaskStarted = Date.now() / 1000 >= taskStartAt;

    const totalScheduledTests = SchedulerService.calculateSchedule(task, testsPerBatch, 10, isTaskStarted).countTests;

    const suppliersOverView = this.buildSuppliersOverview(suppliers);

    const taskOverview: TaskSettingDetail = {
      id: 'taskOverview',
      title: 'Task overview',
      data: [
        ['Task ID', task.id],
        ['Task name', task.title],
        ['Created date', moment(task.createdAt).format('DD MMM HH:mm')],
        ['Description', task.description],
        ['Start date', moment(task.startAt).format('DD MMM HH:mm')],
        ['Finish date', task.runUntilDisabled ? 'Run until disabled' : moment(task.finishAt).format('DD MMM HH:mm')],
        ['Status', this.formatState(task)],
        ['Repeat every', this.formatDuration(task)],
        ['Repeat days', sortedDays.length === 7 ? 'Everyday' : sortedDays.join(', ')],
        ['Time (From-Till)', this.getTimeValues(task)],
        ['Total scheduled tests', totalScheduledTests],
        ['Total executed tests', totalExecutedTests],
      ],
    };
    if (task.sendOnlyOnce) {
      taskOverview.data = taskOverview.data.filter(_ => !['Finish date', 'Repeat every', 'Repeat days', 'Time (From-Till)'].includes(_[0]));
    }
    let contentText = template.contentTexts[0];
    if (!contentText) {
      const telqTextDetails = template.dynamicTexts[0];
      contentText = {
        senderId: template.senders[0],
        text: telqTextDetails.text,
        telqIdType: telqTextDetails.telqIdType,
        telqIdCase: telqTextDetails.telqIdCase,
        telqIdLength: telqTextDetails.telqIdLength,
      };
    }
    const contentTemplateOverView: TaskSettingDetail = {
      id: 'contentTemplateOverView',
      title: 'Content template',
      data: [
        ['Sender', contentText.senderId],
        ['Text', contentText.text],
        ['TelQ ID type', contentText.telqIdType],
        ['TelQ ID case', contentText.telqIdCase],
        ['TelQ ID length', contentText.telqIdLength],
      ],
    };
    if (template.flashCallDetection) {
      contentTemplateOverView.data.push(['Flash call detection', 'Enabled']);
    }
    const destinationDtos = template.destinationDtos;
    const countriesOverView: TaskSettingDetail = {
      id: 'contriesOverView',
      title: 'Country/Network',
      data: [
        [
          'Country (MCC) / Network (MNC)',
          destinationDtos
            .map(dto => {
              if (dto.manualNumber) {
                return `<span title="Manual number">${dto.phone}</span>`;
              }
              return TestResultUtils.renderDestination(dto, true);
            })
            .join('<br>'),
        ],
      ],
    };

    if (report) {
      const supplierOverView = this.buildReportSupplierOverview(report.supplierDto);

      const reasonForReportOverView: TaskSettingDetail = {
        id: 'reasonForReportOverView',
        title: 'Reason for report',
        data: [['Reason for report', SchedulerService.formatReportRule(report.reportRuleDto)]],
      };
      return [taskOverview, contentTemplateOverView, countriesOverView, supplierOverView, reasonForReportOverView];
    } else {
      const reasonForReportOverView: TaskSettingDetail = {
        id: 'reasonForReportOverView',
        title: 'Reason for report',
        data: [
          ['Reason for report', this.getReportRulesForTask(task.reportRuleDtos)],
          ['Report emails', task.reportEmails.join(', ')],
        ],
      };
      return [taskOverview, contentTemplateOverView, countriesOverView, suppliersOverView, reasonForReportOverView];
    }
  }

  getTimeValues(task: ScheduledTask): string {
    let value = `${CustomUtils.decimalToTimeFormat(Number(task.fromHour))} - ${CustomUtils.decimalToTimeFormat(Number(task.tillHour))}`;
    if (!CustomUtils.isOffsetInBrowserTimezone(task.zoneOffsetSec)) {
      value += '<span class="icon-help-1 text-neutral-n6 ms-10 fs-3" title="The timezone where this task was created is different."></span>';
    }
    return `<div class="d-flex align-items-center"> ${value}</div>`;
  }

  getReportRulesForTask(reportRules: ScheduledTaskReportRule[]): string {
    return reportRules.map(rule => SchedulerService.formatReportRule(rule)).join('');
  }

  formatState(task: ScheduledTask): string {
    switch (task.state) {
      case ScheduledTaskState.SCHEDULED:
        return `<div class="one-line text-accent-a6"><i class="fas fa-history me-0_8"></i>Scheduled</div>`;
      case ScheduledTaskState.RUNNING:
        return `<div class="one-line text-primary-p4"><i class="icon-wait me-0_8"></i>Running</div>`;
      case ScheduledTaskState.STOPPED:
        return `<div class="one-line text-error-r2"><i class="fas fa-stop me-0_9"></i>Stopped</div>`;
      case ScheduledTaskState.PAUSED:
        return `<div class="one-line text-warning-w3"><i class="fas fa-pause me-0_8"></i>Paused</div>`;
      case ScheduledTaskState.FINISHED:
        return `<div class="one-line"><i class="icon-check me-0_7"></i>Completed</div>`;
      default:
        return '';
    }
  }

  create(): ScheduledTask {
    return {
      id: null,
      title: '',
      sendOnlyOnce: true,
      state: null,
      repeatEvery: 60 * 5,
      reportFrequency: 'ALL',
      reportEmails: [],
      daysOfWeek: [],
      reportRules: [],
      userId: null,
      testCaseTemplateId: null,
      fromHour: null,
      tillHour: null,
      reportPerAlertEnabled: true,
      allAlertXlsReportEnabled: false,
      repetitionAlertXlsReportEnabled: false,
      allTestXlsReportEnabled: false,
      testRepeatsPerIteration: 1,
      callbackUrl: null,
      coverageNotificationEnabled: false,
    };
  }

  getHiddenMode(taskView: SchedulerTaskView): boolean {
    if (taskView !== 'inactive') {
      return false;
    }

    return this.storage.get(SchedulerService.HIDDEN_STORAGE_KEY, false);
  }

  saveHiddenMode(taskView: SchedulerTaskView, hidden: boolean) {
    if (taskView !== 'inactive') {
      return;
    }

    this.storage.set(SchedulerService.HIDDEN_STORAGE_KEY, hidden);
  }

  getShowMyTasksFlag(taskView: SchedulerTaskView): boolean {
    return this.storage.get(this.getShowMyTasksStorageKey(taskView), false);
  }

  saveShowMyTasksFlag(taskView: SchedulerTaskView, showMyTasks: boolean) {
    this.storage.set(this.getShowMyTasksStorageKey(taskView), showMyTasks);
  }

  getEmptySearchObject(): SchedulerSearchEvent {
    return {
      ids: null,
      statuses: null,
      names: null,
      startDateFrom: null,
      startDateTo: null,
      finishDateFrom: null,
      finishDateTo: null,
      countryNetworks: null,
      suppliers: null,
      schedulerUserIds: null,
      hidden: null,
      ownTasksOnly: null,
    };
  }

  getStatusesForTaskView(taskView: SchedulerTaskView): string[] {
    return taskView === 'inactive' ? [...SchedulerService.INACTIVE_SCHEDULER_STATUSES] : [...SchedulerService.ACTIVE_SCHEDULER_STATUSES];
  }

  normalizeStatusesForTaskView(statuses: string[] | null | undefined, taskView: SchedulerTaskView): string[] {
    const allowedStatuses = this.getStatusesForTaskView(taskView);
    const filteredStatuses = (statuses ?? []).filter(status => allowedStatuses.includes(status));

    return filteredStatuses.length ? filteredStatuses : allowedStatuses;
  }

  normalizeSearchDataForTaskView(
    searchData: Partial<SchedulerSearchEvent> | null | undefined,
    taskView: SchedulerTaskView,
    hidden: boolean,
    ownTasksOnly: boolean
  ): SchedulerSearchEvent {
    const normalizedSearchData = {
      ...this.getEmptySearchObject(),
      ...(searchData ?? {}),
    };

    normalizedSearchData.statuses = this.normalizeStatusesForTaskView(normalizedSearchData.statuses, taskView);
    normalizedSearchData.hidden = taskView === 'inactive' ? hidden : false;
    normalizedSearchData.ownTasksOnly = ownTasksOnly;

    return normalizedSearchData;
  }

  private getShowMyTasksStorageKey(taskView: SchedulerTaskView): string {
    return `${SchedulerService.SHOW_MY_TASKS_FLAG_KEY_PREFIX}-${taskView}`;
  }

  static canStopTask(task: ScheduledTask): boolean {
    const state = task.state;
    if (!state) return false;

    return [ScheduledTaskState.RUNNING, ScheduledTaskState.PAUSED, ScheduledTaskState.SCHEDULED].includes(state);
  }
  static canPauseTask(task: ScheduledTask): boolean {
    const state = task.state;
    if (!state) return false;
    return [ScheduledTaskState.RUNNING, ScheduledTaskState.SCHEDULED].includes(state);
  }
  static canResumeTask(task: ScheduledTask): boolean {
    const state = task.state;
    if (!state) return false;

    const finishAt = typeof task.finishAt === 'string' ? Date.parse(task.finishAt) : task.finishAt;
    const isNotFinishedYet = task.sendOnlyOnce || !finishAt || Date.now() < finishAt;

    return [ScheduledTaskState.PAUSED].includes(state) && isNotFinishedYet;
  }
  static canHideTask(task: ScheduledTask): boolean {
    const state = task.state;
    if (!state) return false;
    return ![ScheduledTaskState.RUNNING, ScheduledTaskState.SCHEDULED, ScheduledTaskState.PAUSED].includes(state);
  }

  static repeatEveryToSeconds(value: number, unit: string): number {
    if (unit === 'unlimited') {
      return null;
    }

    const map = {
      s: 1,
      m: 60,
      h: 3600,
      d: 3600 * 24,
    };

    return value * map[unit];
  }

  static secondsToHuman(seconds: number) {
    if (seconds === null) {
      return { value: null, unit: 'unlimited' };
    }
    if (seconds < 60) {
      return { value: seconds, unit: 's' };
    }
    if (seconds < 3600) {
      return { value: seconds / 60, unit: 'm' };
    }
    if (seconds < 3600 * 24) {
      return { value: seconds / 3600, unit: 'h' };
    }

    return { value: seconds / (3600 * 24), unit: 'd' };
  }

  static calculateSchedule(task: ScheduledTask, countTests = 0, limit = 10, calculateForStartedTask = false): TaskScheduleDef {
    const result = {
      countTasks: 0,
      countTests: 0,
      history: [],
    };

    if (task.sendOnlyOnce) {
      if (!task.startAt) {
        return result;
      }
      const startAt = typeof task.startAt === 'number' ? task.startAt : Date.parse(task.startAt) / 1000;
      return {
        countTasks: 1,
        countTests: countTests,
        history: [new Date(startAt * 1000)],
      };
    }

    if (task.startAt && !task.finishAt) {
      const startAt = typeof task.startAt === 'number' ? task.startAt : Date.parse(task.startAt) / 1000;
      return {
        countTasks: 1,
        countTests: countTests,
        history: [new Date(startAt * 1000)],
      };
    }

    if (!parseInt(String(task.repeatEvery)) || !task.startAt || !task.finishAt || !task.daysOfWeek || !task.daysOfWeek.length) {
      return result;
    }

    const startAt = typeof task.startAt === 'number' ? task.startAt : Date.parse(task.startAt) / 1000;
    const finishAt = typeof task.finishAt === 'number' ? task.finishAt : Date.parse(task.finishAt) / 1000;
    const taskCreatedAt = typeof task.createdAt === 'number' ? task.createdAt : Date.parse(task.createdAt) / 1000;
    const startCountdown = taskCreatedAt > startAt ? taskCreatedAt : 0;
    const now = calculateForStartedTask ? startCountdown : Date.now() / 1000;
    const scheduleOffsetSec = typeof task.zoneOffsetSec === 'number' ? task.zoneOffsetSec : -new Date().getTimezoneOffset() * 60;

    task.fromHour = task.fromHour || 0;
    task.tillHour = task.tillHour || 24;
    const taskFromHour = typeof task.fromHour === 'number' ? task.fromHour : +task.fromHour.replace(':', '.');
    const taskTillHour = typeof task.tillHour === 'number' ? task.tillHour : +task.tillHour.replace(':', '.');
    const hoursCheck = taskFromHour >= 0 && taskTillHour >= 0;

    if (!calculateForStartedTask && finishAt <= now) {
      return result;
    }

    const dayMap = {
      SUNDAY: 0,
      MONDAY: 1,
      TUESDAY: 2,
      WEDNESDAY: 3,
      THURSDAY: 4,
      FRIDAY: 5,
      SATURDAY: 6,
    };

    let repeatEveryRecalculated = task.repeatEvery;
    const validDays = task.daysOfWeek.map(_ => dayMap[_]);

    // Calculating total task number for randomized schelued task
    if (task.randomized) {
      const fromHoursMinutes = CustomUtils.parseHourToHoursMinutes(task.fromHour);
      const tillHoursMinutes = CustomUtils.parseHourToHoursMinutes(task.tillHour);

      const currentTime = calculateForStartedTask ? new Date((taskCreatedAt > startAt ? taskCreatedAt : startAt) * 1000) : new Date();
      const configuredStart = new Date(startAt * 1000);

      // checks if the configured start time actually earlier than the current time
      // in this case the actual start should be chosen as current time
      const start = configuredStart < currentTime ? currentTime : configuredStart;
      const finish = new Date(finishAt * 1000);

      const tillHourToday = new Date(start.getTime());
      tillHourToday.setHours(parseInt(tillHoursMinutes[0]));
      tillHourToday.setMinutes(parseInt(tillHoursMinutes.length === 1 ? '0' : tillHoursMinutes[1]));
      tillHourToday.setSeconds(0);
      tillHourToday.setMilliseconds(0);

      const fromHourToday = new Date(start.getTime());
      fromHourToday.setHours(parseInt(fromHoursMinutes[0]));
      fromHourToday.setMinutes(parseInt(fromHoursMinutes.length === 1 ? '0' : fromHoursMinutes[1]));
      fromHourToday.setSeconds(0);
      fromHourToday.setMilliseconds(0);

      // We choose from hour as start time if it is later than the current time.
      // This means that the test is expected to start later than scheduled.
      const lastScheduledTime = new Date(fromHourToday.getTime());

      // calculate periods between tests in seconds
      repeatEveryRecalculated = (tillHourToday.getTime() - fromHourToday.getTime()) / 1000 / task.repeatsPerDayRandomized;

      const differenceMinutes = (tillHourToday.getTime() - fromHourToday.getTime()) / (60 * 1000);
      if (task.repeatsPerDayRandomized > differenceMinutes) {
        return result;
      }

      if (!calculateForStartedTask && currentTime >= finish) {
        return result;
      }

      const ONE_DAY_MILLIS = 24 * 3600 * 1000;

      let lastScheduledTimeMillis = lastScheduledTime.getTime();
      let fromHourTodayMillis = fromHourToday.getTime();
      let tillHourTodayMillis = tillHourToday.getTime();

      while (lastScheduledTimeMillis < finish.getTime()) {
        // Skip calculating values in the past
        if (lastScheduledTimeMillis < start.getTime()) {
          lastScheduledTimeMillis += repeatEveryRecalculated * 1000;
          continue;
        }

        // Check if the current day should not be considered and the time of the last scheduled task
        // is not later than the till hour, otherwise skips calculations for the current day and shifts to the next day
        if (lastScheduledTimeMillis >= tillHourTodayMillis || validDays.indexOf(new Date(lastScheduledTimeMillis).getDay()) === -1) {
          fromHourTodayMillis += ONE_DAY_MILLIS;
          tillHourTodayMillis += ONE_DAY_MILLIS;
          lastScheduledTimeMillis = fromHourTodayMillis;
          continue;
        }

        // if latest scheduled task is before the tillHour and finish time
        // we increase counter of tests and tasks
        if (lastScheduledTimeMillis < tillHourTodayMillis) {
          result.countTasks++;
          result.countTests += countTests;
          lastScheduledTimeMillis += repeatEveryRecalculated * 1000;
        }

        if (task.runUntilDisabled) {
          return {
            countTasks: result.countTasks > 0 ? 'Unlimited' : 0,
            countTests: result.countTests > 0 ? 'Unlimited' : 0,
            history: [],
          };
        }
      }
      return result;
    }

    const next = (task: ScheduledTask, start: number): Date => {
      while (true) {
        start += repeatEveryRecalculated;
        if (start >= finishAt) {
          break;
        }
        const startDate = new Date(start * 1000);
        const taskZoneStartDate = new Date((start + scheduleOffsetSec) * 1000);
        if (validDays.indexOf(taskZoneStartDate.getUTCDay()) === -1) {
          continue;
        }
        const h = taskZoneStartDate.getUTCHours();
        const m = String(taskZoneStartDate.getUTCMinutes()).padStart(2, '0');
        const hm = +`${h}.${m}`;
        if (hoursCheck && !(hm >= taskFromHour && hm <= taskTillHour)) {
          continue;
        }
        return startDate;
      }
      return null;
    };

    let startNow = now <= startAt ? startAt - repeatEveryRecalculated : startAt,
      nextAt: Date = null;
    while (true) {
      nextAt = next(task, startNow);
      if (nextAt === null) {
        break;
      }
      startNow = nextAt.getTime() / 1000;
      if (now >= startNow) {
        continue;
      }
      if (limit && result.history.length < limit) {
        result.history.push(nextAt);
      }
      result.countTasks++;
      if (countTests) {
        result.countTests += countTests;
      }
      if (task.runUntilDisabled && result.history.length >= limit) {
        break;
      }
    }
    if (task.runUntilDisabled) {
      return {
        countTasks: result.history.length > 0 ? 'Unlimited' : 0,
        countTests: result.history.length > 0 ? 'Unlimited' : 0,
        history: result.history,
      };
    }
    return result;
  }

  private getTestsPerBatch(task: ScheduledTask, template: TestCaseTemplate): number {
    const destinationsCount = template.destinationDtos?.length ?? 0;
    const routesCount = template.routeDtos?.length ?? 0;
    const contentTextsCount = template.contentTexts?.length ? template.contentTexts.length : this.getDynamicContentTextsCount(template);
    const repeatsPerIteration = task.testRepeatsPerIteration ? task.testRepeatsPerIteration : 1;
    return destinationsCount * routesCount * contentTextsCount * repeatsPerIteration;
  }

  private getDynamicContentTextsCount(template: TestCaseTemplate): number {
    const dynamicTextsCount = template.dynamicTexts?.length ?? template.dynamicTextDtos?.length ?? 0;
    if (!dynamicTextsCount) {
      return 0;
    }

    const sendersCount = template.senders?.length ?? 0;

    if (sendersCount === dynamicTextsCount) {
      return sendersCount;
    }

    return sendersCount > 0 ? dynamicTextsCount * sendersCount : dynamicTextsCount;
  }

  private buildSuppliersOverview(suppliers: Supplier[]): TaskSettingDetail {
    return {
      id: 'suppliersOverView',
      title: `${suppliers.length > 1 ? 'Suppliers' : 'Supplier'} info`,
      data: this.buildSuppliersOverviewData(suppliers),
    };
  }

  private buildSuppliersOverviewData(suppliers: Supplier[]): [string, unknown][] {
    if (!suppliers.length) {
      return [['Supplier', '-']];
    }

    return suppliers.map((supplier, index) => [
      suppliers.length > 1 ? `Supplier ${index + 1}` : 'Supplier',
      `<p class="mb-0_4 d-flex align-items-center">${supplier.title} ${supplier.routeType} (id: ${supplier.id})</p>`,
    ]);
  }

  private buildReportSupplierOverview(supplier: Supplier): TaskSettingDetail {
    return {
      id: 'supplierOverView',
      title: 'Supplier info',
      data: [['Supplier', `<p class="mb-0_4 d-flex align-items-center">${supplier.title} ${supplier.routeType} (id: ${supplier.id})</p>`]],
    };
  }
}

export interface TaskScheduleDef {
  countTasks: number | string;
  countTests: number | string;
  history: Date[];
}

export interface CombinationRuleItem {
  id: number;
  messageState: string;
  messageStateWait: number;
  messageStateWaitUnit: string;
  messageStatusDropdownShow?: boolean;
  testStatus: string;
  testStatusDropdownShow?: boolean;
  testStatusWait: number;
  testStatusWaitUnit: string;
  repetitionsTarget: number;
  repetitionsTargetDropdownShow?: boolean;
  isConsecutive: boolean;
  isConsecutiveDropdownShow?: boolean;
}

export interface StatusItem {
  id: string;
  isSelectedInCombination?: boolean;
  label: string;
  selected?: boolean;
  wait: number | null;
  unit: string | null;
}

export class AllRequestParams {}

export class SchedulerRequestParams {
  size = 20;
  page = 1;
  search: string;
  searchData: SchedulerSearchEvent;

  hidden = false;
  ownTasksOnly = false;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}

export class SchedulerReportRequestParams {
  size = 20;
  page = 1;
  search: string;
  searchData: SchedulerReportSearchEvent;

  hidden = false;
  ownTasksOnly = false;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}

export interface SchedulerSearchEvent {
  ids: string[];
  statuses: string[];
  names: string[];
  startDateFrom: string;
  startDateTo: string;
  finishDateFrom: string;
  finishDateTo: string;
  countryNetworks: string[];
  suppliers: string[];
  schedulerUserIds: number[];
  hidden: boolean;
  ownTasksOnly: boolean;
}

export type SchedulerTaskView = 'active' | 'inactive';

export interface SchedulerReportSearchEvent {
  ids: string[];
  testIds: string[];
  title: string;
  scheduleName: string;
  testNumbers: string[];
  createAtFrom: string;
  createdAtTo: string;
}

export type SchedulerTaskActionType = 'pause' | 'hide' | 'stop' | 'clone' | 'resume' | 'testResults' | 'settings' | 'visible';

export interface SchedulerTaskActionData {
  name: SchedulerTaskActionType;
  row: ScheduledTask;
  column: string;
}

export interface SchedulerReportActionData {
  name: string;
  row: Report;
  column: string;
}

export interface SchedulerStatus {
  label: string;
  value: string;
  isChecked: boolean;
  icon: string;
}

export interface TaskSettingDetail {
  id: string;
  title: string;
  data: [string, unknown][];
}
